// Main Login Page
import React, { useState } from 'react';
import Logo from '../../../assets/fonts/Solo-logo.png';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../config/firebase/auth';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import RecoverPasswordForm from './RecoverPasswordForm';
import { ArrowRight, Lock, Mail, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';

type AuthStep = 'email' | 'password' | 'success';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<AuthStep>('email');
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecover, setShowRecover] = useState(false);

  // Email validation regex
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(credentials.email)) {
      setError("Por favor ingresa un correo electrónico válido.");
      return;
    }
    setError(null);
    setLoading(true);

    // Simulate API check
    setTimeout(() => {
      setLoading(false);
      setStep('password');
    }, 600);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      const user = userCredential.user;
      
      setStep('success');
      
      // Get user data and navigate after success animation
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      setTimeout(() => {
        if (userSnap.exists()) {
          // Check if user is super admin (by email)
          const SUPER_ADMIN_EMAILS = ['yappppp.adm@gmail.com'];
          if (SUPER_ADMIN_EMAILS.includes(user.email || '')) {
            console.log('✅ Super admin detectado, redirigiendo a /super-admin');
            navigate('/super-admin');
          } else {
            console.log('✅ Usuario regular detectado, redirigiendo a /admin');
            navigate('/admin');
          }
        } else {
          setError('No se encontró un perfil de usuario.');
          setStep('password');
        }
        setLoading(false);
      }, 1500);
    } catch (error: any) {
      setLoading(false);
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        setError('Contraseña incorrecta');
      } else if (error.code === 'auth/user-not-found') {
        setError('Usuario no encontrado');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Intenta más tarde.');
      } else {
        setError('Error al iniciar sesión: ' + (error.message || error));
      }
    }
  };

  const goBack = () => {
    setStep('email');
    setError(null);
    setCredentials(prev => ({ ...prev, password: '' }));
  };

  if (showRecover) {
    return (
      <RecoverPasswordForm
        onBack={() => {
          setShowRecover(false);
          setCredentials(f => ({ ...f, password: '' }));
          setError(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden bg-yap-bg text-white selection:bg-yap-primary selection:text-black">
      
      {/* Background Ambience */}
      <div className="absolute top-[-20%] left-[20%] w-[600px] h-[600px] bg-yap-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Darker overlay when on password/success step */}
      {(step === 'password' || step === 'success') && (
        <div className="fixed inset-0 bg-black/40 z-[5] transition-opacity duration-500" />
      )}

      {/* Main Card (scaled slightly down) */}
      <div className="ui-scale-80 w-full max-w-[480px] bg-zinc-900/40 backdrop-blur-2xl rounded-[32px] border border-white/5 shadow-2xl relative z-10 overflow-hidden">
        
        <div className="p-8 sm:p-10">
          {/* Header */}
          <div className="flex flex-col items-center mb-10">
            <img src={Logo} alt="YAP Logo" className="w-20 h-20 mb-6 object-contain" />
            <h1 className="text-3xl font-bold text-white tracking-tight">
              {step === 'success' ? 'Bienvenido de nuevo' : 'Iniciar sesión'}
            </h1>
            <p className="text-zinc-400 text-sm mt-3 text-center leading-relaxed">
              {step === 'email' && "Ingresa tu correo electrónico para acceder."}
              {step === 'password' && (
                <span className="text-white font-medium bg-zinc-800/50 px-3 py-1 rounded-full text-xs border border-white/5">{credentials.email}</span>
              )}
              {step === 'success' && "Iniciando sesión de forma segura..."}
            </p>
          </div>

          {/* Form Content */}
          <div className="relative min-h-[160px]">
            
            {/* Step 1: Email */}
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <div className={`group relative transition-all duration-300 ${error ? 'animate-shake' : ''}`}>
                    <div className="relative">
                      <Mail className="absolute left-4 top-4 h-5 w-5 text-zinc-500 group-focus-within:text-yap-primary transition-colors duration-300" />
                      <input
                        id="email"
                        type="email"
                        autoFocus
                        autoComplete="email"
                        value={credentials.email}
                        onChange={(e) => {
                          setCredentials({ ...credentials, email: e.target.value });
                          if (error) setError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && credentials.email) {
                            e.preventDefault();
                            handleEmailSubmit(e as any);
                          }
                        }}
                        className={`w-full pl-12 pr-4 py-4 bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 focus:border-yap-primary rounded-2xl outline-none transition-all duration-300 font-medium text-white placeholder:text-zinc-400 focus:ring-1 focus:ring-yap-primary/50 autofill:bg-zinc-900/50 autofill:text-white [&:-webkit-autofill]:bg-zinc-900/50 [&:-webkit-autofill]:text-white [&:-webkit-autofill]:[-webkit-text-fill-color:white] ${
                          error ? '!border-red-500/50 focus:!border-red-500' : ''
                        }`}
                        placeholder="usuario@email.com"
                        style={{
                          WebkitTextFillColor: 'text-white placeholder:text-zinc-600 focus:ring-1/50 focus:ring-yap-primary',
                        }}
                      />
                    </div>
                  </div>
                  {error && <p className="text-red-400 text-xs pl-2 font-medium">{error}</p>}
                </div>

                <button
                  type="submit"
                  disabled={!credentials.email || loading}
                  className="w-full bg-yap-primary hover:bg-yap-hover text-black font-bold py-4 rounded-2xl shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)] hover:shadow-[0_0_25px_-5px_rgba(0,255,132,0.5)] transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2 text-[15px]"
                >
                  {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <>Continuar <ArrowRight size={18} strokeWidth={2.5} /></>}
                </button>
              </form>
            )}

            {/* Step 2: Password */}
            {step === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <div className="group relative">
                    <div className="relative">
                      <Lock className="absolute left-4 top-4 h-5 w-5 text-zinc-500 group-focus-within:text-yap-primary transition-colors duration-300" />
                      <input
                        id="password"
                        type="password"
                        autoFocus
                        value={credentials.password || ''}
                        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                        className="w-full pl-12 pr-4 py-4 bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 focus:border-yap-primary rounded-2xl outline-none transition-all duration-300 font-medium text-white placeholder:text-zinc-600 focus:ring-1 focus:ring-yap-primary/50"
                        placeholder="Ingresa tu contraseña"
                      />
                    </div>
                  </div>
                  {error && (
                    <p className="text-red-400 text-xs pl-2 font-medium">{error}</p>
                  )}
                  <div className="flex justify-between items-center text-xs px-2 mt-2">
                    <button type="button" onClick={goBack} className="text-zinc-500 hover:text-white flex items-center gap-1.5 transition-colors font-medium">
                      <ArrowLeft size={12} /> ¿Correo incorrecto?
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowRecover(true)}
                      className="text-yap-primary hover:text-yap-hover font-medium transition-colors"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!credentials.password || loading}
                  className="w-full bg-yap-primary hover:bg-yap-hover text-black font-bold py-4 rounded-2xl shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)] hover:shadow-[0_0_25px_-5px_rgba(0,255,132,0.5)] transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2 text-[15px]"
                >
                   {loading ? <Loader2 className="animate-spin w-5 h-5" /> : "Iniciar sesión"}
                </button>
              </form>
            )}

            {/* Step 3: Success */}
            {step === 'success' && (
              <div className="flex flex-col items-center justify-center h-full animate-slide-up py-6">
                <div className="w-20 h-20 bg-yap-primary/10 rounded-full flex items-center justify-center mb-6 ring-1 ring-yap-primary/30">
                  <CheckCircle className="w-10 h-10 text-yap-primary" />
                </div>
                <div className="mt-4 w-48 bg-zinc-800/50 h-1 rounded-full overflow-hidden">
                  <div className="h-full bg-yap-primary w-full origin-left animate-grow"></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-zinc-900/30 p-5 border-t border-white/5 text-center backdrop-blur-md">
          <p className="text-xs text-zinc-500 font-medium">
            Protegido por reCAPTCHA · <a href="#" className="hover:text-yap-primary transition-colors">Privacidad</a> · <a href="#" className="hover:text-yap-primary transition-colors">Términos</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;